import React, {useEffect, useState} from 'react';
import {DialogContent, Stack, Typography, Link, IconButton, Divider} from "@mui/material";
import LoginFields from '../forms/LoginFields';
import {
    Formik,
    Form
} from 'formik';
import logInModel, {logInModelType} from '../forms/models/logInModel';
import logInSchema from '../forms/schemas/logInSchema';
import * as Yup from 'yup';
import {usePostUsersLoginMutation} from '../../redux/residentPortalApi'
import LoadingCover from '../common/LoadingCover'
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import authSlice from "../../redux/reducers/authSlice";
import { Close, LockOutlined } from '@mui/icons-material';
import useIsMobile from '../../hooks/useIsMobile';
import { StyledDialogTitle, IconContainer, LoginIcon, StyledButton } from '../common/styledComponents/AuthComponents';
import {useRouter} from '../../hooks/useRouter';

const { formField: {logInEmail} } = logInModel;

type MyFormValues = logInModelType

const logInModelInitial = logInModel.initialValues

const LogInFormSchema = Yup.object().shape({...logInSchema})

interface OwnProps {
    close: () => void;
}

const LoginForm = (props: OwnProps) => {
    const {close} = props
    const dispatch = useAppDispatch();
    const router = useRouter();
    const isMobile = useIsMobile();
    const [updateUserLogin, {isLoading, isSuccess, isError, error}] = usePostUsersLoginMutation()
    const [loginError, setLoginError] = useState('')
    const initialEmail = useAppSelector(state => state.auth.initialEmail)
    const initialValues:MyFormValues = {
        ...logInModelInitial,
        [logInEmail.name]: initialEmail
    };
    const authMessage = useAppSelector(state => state.auth.authMessage);

    const handleSubmit = async (values: MyFormValues) => {
        //here we will handle sending the form through to the api
        const body = {email: values.logInEmail, password: values.logInPass}
        updateUserLogin({body})
    }

    useEffect(() => {
        if(isSuccess) {
            setTimeout(() => {
                close()
            }, 300)
        }
    }, [isSuccess, close])

    useEffect(() => {
        if(isError && error) {
            const err:any = error
            if(err?.data?.error?.message) {
                setLoginError(err?.data?.error?.message)
            }
        }
        else {
            setLoginError('')
        }
    }, [isError, error])

    function openReset() {
        dispatch(authSlice.actions.setAuthState('reset'))
    }

    function openBnbGuestPortal() {
        close();
        router.push('/bnb-guest-portal');
    }

    return (
        <React.Fragment>
            <StyledDialogTitle
                data-cy="dialogue-title" 
                sx={{
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center',
                    justifyContent: isMobile ? 'space-between' : 'center'
                }}
            >
                <Typography component="span" variant="h5" fontWeight="600" color={'white'}>
                    Welcome Back
                </Typography>
                {
                    isMobile ?
                        <IconButton
                            onClick={close}
                            sx={{ color: 'white' }}
                        >
                            <Close
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: 24
                                }}
                            />
                        </IconButton> : null
                }
            </StyledDialogTitle>

            <DialogContent sx={{ padding: '24px', paddingBottom: '32px' }}>
                <IconContainer>
                    <LoginIcon>
                        <LockOutlined sx={{ color: 'white', fontSize: 28 }} />
                    </LoginIcon>
                </IconContainer>
                
                <Typography 
                    align="center" 
                    variant="h6" 
                    fontWeight="500" 
                    sx={{ marginBottom: '16px' }}
                >
                    Sign in to your account
                </Typography>
                
                {authMessage && (
                    <Typography 
                        align="center" 
                        variant="body2" 
                        color="primary" 
                        sx={{ marginBottom: '16px' }}
                    >
                        {authMessage}
                    </Typography>
                )}
                
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={LogInFormSchema}
                >
                    <Form>
                        <Stack spacing={4} width="100%" alignItems="center">
                            <LoginFields apiError={loginError}/>

                            <StyledButton
                                variant="contained"
                                type="submit"
                                fullWidth
                                size="large"
                                data-cy={"loginSubmit"}
                            >
                                Sign In
                            </StyledButton>

                            <Divider sx={{ width: '100%', margin: '8px 0' }} />

                            <Stack spacing={2} width="100%" alignItems="center">
                                <Typography align="center" variant="body2">
                                    <Link 
                                        onClick={openReset}
                                        sx={{ 
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Forgot your password?
                                    </Link>
                                </Typography>
                                <Typography align="center" variant="body2">
                                    <Link
                                        onClick={openBnbGuestPortal}
                                        sx={{
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Guest? Invite your visitors here.
                                    </Link>
                                </Typography>
                            </Stack>
                        </Stack>
                    </Form>
                </Formik>
            </DialogContent>
            {
                (isLoading || isSuccess) &&
                    <LoadingCover isSuccess={isSuccess} />
            }
        </React.Fragment>
    )
}

export default LoginForm;
